/*
 * JANGIN — cart-swap custom element
 *
 * Handles "Add" / "Swap to set" actions in the cart drawer cross-sell.
 * Reads these data attributes from the <cart-swap> element:
 *   data-remove-keys   — pipe-separated cart line-item keys to remove
 *                        (may be empty for plain add actions)
 *   data-add-variant-id — variant ID to add
 *   data-add-qty        — quantity to add (default 1)
 *
 * Sequence: remove keys in parallel, add new item, then re-render the
 * cart drawer section. Atomicity is best-effort; Shopify's cart API
 * does not expose a single transactional multi-op endpoint, so on
 * failure mid-sequence the cart may be in an intermediate state —
 * we surface the error and the drawer re-render reflects actual state.
 */

if (!customElements.get('cart-swap')) {
  class CartSwap extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector('[data-swap-action]');
      if (button) button.addEventListener('click', this.onClick.bind(this));
    }

    async onClick(event) {
      event.preventDefault();
      this.setAttribute('aria-busy', 'true');

      const removeKeys = (this.dataset.removeKeys || '').split('|').filter(Boolean);
      const addVariantId = parseInt(this.dataset.addVariantId, 10);
      const addQty = parseInt(this.dataset.addQty || '1', 10);

      try {
        if (removeKeys.length > 0) {
          await Promise.all(
            removeKeys.map((key) =>
              fetch(`${routes.cart_change_url}`, {
                ...fetchConfig(),
                body: JSON.stringify({ id: key, quantity: 0 }),
              })
            )
          );
        }

        if (addVariantId) {
          await fetch(`${routes.cart_add_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [{ id: addVariantId, quantity: addQty }],
            }),
          });
        }

        await this.refreshDrawer();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err) {
        console.error('cart-swap failed:', err);
        this.removeAttribute('aria-busy');
      }
    }

    async refreshDrawer() {
      const [drawerRes, bubbleRes] = await Promise.all([
        fetch(`${routes.cart_url}?section_id=cart-drawer`),
        fetch(`${routes.cart_url}?section_id=cart-icon-bubble`),
      ]);
      const [drawerHtml, bubbleHtml] = await Promise.all([drawerRes.text(), bubbleRes.text()]);

      const parser = new DOMParser();

      const newDrawerDoc = parser.parseFromString(drawerHtml, 'text/html');
      const newDrawer = newDrawerDoc.querySelector('cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');
      if (newDrawer && currentDrawer) {
        const wasActive = currentDrawer.classList.contains('active');
        currentDrawer.innerHTML = newDrawer.innerHTML;
        currentDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        if (wasActive) currentDrawer.classList.add('active');
      }

      const newBubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
      const newBubble = newBubbleDoc.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }
  }

  customElements.define('cart-swap', CartSwap);
}
